import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../csv-export/csv-export/csv-column';
import { ZeitauswahlChange } from '../zeitauswahl/zeitauswahl-change';
import { BwaKonto, BwaSummen, BwaZeile } from './bwa';

@Component({
    selector: 'kiene-bwa-report',
    templateUrl: './bwa-report.component.html',
    styleUrls: ['./bwa-report.component.scss'],
})
export class BwaReportComponent implements OnInit, AfterViewInit {
    displayedColumns = ['bezeichnung', 'betrag', 'prozent', 'belege'];
    displayedKontoColumns = ['konto_nr', 'konto_bezeichnung', 'bwa_gruppe', 'quelle', 'betrag', 'prozent', 'belege'];

    dataSource = new MatTableDataSource<BwaZeile>([]);
    kontoDataSource = new MatTableDataSource<BwaKonto>([]);
    @ViewChild('bwaSort') sort: MatSort;
    @ViewChild('kontoSort') kontoSort: MatSort;

    private allZeilen: BwaZeile[] = [];
    private allKonten: BwaKonto[] = [];
    zeiteintrag: ZeitauswahlChange;
    summen: BwaSummen | null = null;
    loading = false;
    sub: Subscription;
    selectedGruppeId: number | null = null;

    nurFreigegebenCtrl = new FormControl(false);
    leistungsdatumCtrl = new FormControl(false);
    mitBefundenCtrl = new FormControl(true);
    mitWareneinsatzCtrl = new FormControl(false);
    nullzeilenCtrl = new FormControl(false);

    chartType = ChartType.PieChart;
    chartColumns = ['Gruppe', 'Betrag'];
    chartData: (string | number)[][] = [['', 0]];
    chartHasData = false;

    csvColumns: CsvColumn[] = [];
    csvFilename = 'BWA.csv';
    csvKontoColumns: CsvColumn[] = [];
    csvKontoFilename = 'BWA-Konten.csv';

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.csvColumns = [
            new CsvColumn('bezeichnung', 'Position'),
            new CsvColumn('seite', 'Art'),
            new CsvColumn('betrag', 'Betrag'),
            new CsvColumn('prozent', '% Umsatz', '%'),
            new CsvColumn('belege', 'Belege'),
            new CsvColumn('positionen', 'Positionen'),
        ];
        this.csvKontoColumns = [
            new CsvColumn('konto_nr', 'Konto'),
            new CsvColumn('bezeichnung', 'Bezeichnung'),
            new CsvColumn('bwa_gruppe', 'BWA-Gruppe'),
            new CsvColumn('quelle', 'Quelle'),
            new CsvColumn('betrag', 'Betrag'),
            new CsvColumn('prozent', '% Umsatz', '%'),
            new CsvColumn('belege', 'Belege'),
            new CsvColumn('positionen', 'Positionen'),
        ];
        this.nurFreigegebenCtrl.valueChanges.subscribe(() => this.loadData());
        this.leistungsdatumCtrl.valueChanges.subscribe(() => this.loadData());
        this.mitBefundenCtrl.valueChanges.subscribe(() => this.loadData());
        this.mitWareneinsatzCtrl.valueChanges.subscribe(() => this.loadData());
        this.nullzeilenCtrl.valueChanges.subscribe(() => this.applyFilter());
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (row, col) => {
            const v = (row as any)[col];
            return typeof v === 'string' ? v.toLowerCase() : v;
        };
        this.kontoDataSource.sort = this.kontoSort;
        this.kontoDataSource.sortingDataAccessor = (row, col) => {
            const key = col === 'konto_bezeichnung' ? 'bezeichnung' : col;
            const v = (row as any)[key];
            return typeof v === 'string' ? v.toLowerCase() : v;
        };
    }

    zeitintervalChanged(event: ZeitauswahlChange): void {
        this.zeiteintrag = event;
        this.loadData();
    }

    loadData(): void {
        if (!this.zeiteintrag?.von || !this.zeiteintrag?.bis) {
            return;
        }
        let params = new HttpParams();
        params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));
        params = params.set('nur_freigegeben', this.nurFreigegebenCtrl.value ? '1' : '0');
        params = params.set('leistungsdatum', this.leistungsdatumCtrl.value ? '1' : '0');
        params = params.set('mit_befunden', this.mitBefundenCtrl.value ? '1' : '0');
        params = params.set('mit_wareneinsatz', this.mitWareneinsatzCtrl.value ? '1' : '0');

        this.loading = true;
        this.sub?.unsubscribe();
        this.sub = this.api.get(this.apiBaseUrl + 'report/read_bwa.php', params).subscribe({
            next: (res: any) => {
                this.allZeilen = res?.records ?? [];
                this.allKonten = res?.konten ?? [];
                this.summen = res?.summen ?? null;
                this.selectedGruppeId = null;
                this.applyFilter();
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('BWA konnte nicht geladen werden.', err);
            },
        });
    }

    selectGruppe(row: BwaZeile): void {
        if (row.typ !== 'gruppe' || row.bwa_gruppe_id == null) {
            return;
        }
        this.selectedGruppeId = this.selectedGruppeId === row.bwa_gruppe_id ? null : row.bwa_gruppe_id;
        this.applyFilter();
    }

    clearGruppeFilter(): void {
        this.selectedGruppeId = null;
        this.applyFilter();
    }

    quelleLabel(quelle: string): string {
        if (quelle === 'abgabebelege') {
            return 'Abgabebelege';
        }
        if (quelle === 'befunde') {
            return 'Befunde';
        }
        return 'Kosten';
    }

    private applyFilter(): void {
        const showZero = !!this.nullzeilenCtrl.value;
        this.dataSource.data = this.allZeilen.filter(
            z => z.typ === 'summe' || showZero || Math.abs(Number(z.betrag) || 0) > 0.004,
        );

        const konten = this.selectedGruppeId == null
            ? this.allKonten
            : this.allKonten.filter(k => k.bwa_gruppe_id === this.selectedGruppeId);
        this.kontoDataSource.data = showZero ? konten : konten.filter(k => Math.abs(Number(k.betrag) || 0) > 0.004);

        this.chartData = [];
        for (const z of this.allZeilen) {
            if (z.typ !== 'gruppe' || z.seite !== 'A') {
                continue;
            }
            const n = Math.abs(Number(z.betrag) || 0);
            if (n <= 0) {
                continue;
            }
            this.chartData.push([z.bezeichnung, n]);
        }
        this.chartHasData = this.chartData.length > 0;
        if (!this.chartHasData) {
            this.chartData = [['Kein Aufwand', 0]];
        }
    }
}
